/**
 * -------------------------------------------------------
 * Cluster Product Configuration
 * -------------------------------------------------------
 *
 * Author      : Harvinder Singh
 * Created On  : 27 Feb 2026
 *
 * Description:
 * ------------
 * Defines product availability per cluster.
 *
 * Each product inside a cluster can have one of 3 states:
 *
 * 1️⃣ "active"
 *      → Product exists and is fully usable.
 *
 * 2️⃣ "inactive"
 *      → Product exists but is not ready to use
 *        (e.g. premium preview / upsell state).
 *
 * 3️⃣ undefined (missing key)
 *      → Product does not exist in that cluster.
 *
 * This file exports:
 * - CLUSTER_PRODUCT_MAP → Pure configuration object
 * - getClusterProductStatus() → Resolver function
 *
 * IMPORTANT:
 * -----------
 * This file contains NO UI logic.
 * It only resolves product availability based on cluster.
 *
 * -------------------------------------------------------
 */

import { useUserStore } from "@/lib/stores";

/* -------------------------------------------------------
   TYPES
------------------------------------------------------- */

/**
 * Supported product identifiers.
 * These must match navigation item IDs
 * and backend product keys.
 */
export type ProductKey =
  | "Marketing"
  | "Commerce"
  | "Service";

/**
 * Supported cluster identifiers.
 * Extend this as more clusters are introduced.
 */
export type ClusterKey =
  | "uat"
  | "app-uat"
  | "app-staging"
  | "staging"
  | "sandbox"
  | "app-sandbox"
  | "cloud"
  | "cloud1"
  | "app"
  | "europe"
  | "canada"
  | "australia";

/**
 * Product availability states inside a cluster.
 */
export type ProductStatus =
  | "active"
  | "inactive";

/* -------------------------------------------------------
   CLUSTER CONFIGURATION (PURE DATA)
------------------------------------------------------- */

/**
 * Defines which products exist inside each cluster
 * and their availability state.
 *
 * Structure:
 * {
 *   clusterName: {
 *     ProductKey: "active" | "inactive"
 *   }
 * }
 *
 * If a product key is NOT present in the cluster,
 * it is considered as non-existent.
 */
export const CLUSTER_PRODUCT_MAP: Record<
  ClusterKey,
  Partial<Record<ProductKey, ProductStatus>>
> = {
  uat: {
    Marketing: "active",
    Commerce: "active",
    Service: "active",
  },

  staging: {
    Marketing: "active",
    Commerce: "active",
    Service: "active",
  },

  "app-uat": {
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "app-staging": {
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  sandbox: {
    Marketing: "active",
    Commerce: "active",
    Service: "active",
  },

  "app-sandbox": {
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "cloud": {
    Marketing: "active",
    Service: "active",
    Commerce: "inactive",
  },

  "cloud1": {
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "app":{
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "europe":{
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "canada":{
    Marketing: "active",
    Service: "inactive",
    Commerce: "inactive",
  },

  "australia":{
    Marketing: "active",
    Service: "active",
    Commerce: "active",
  },

};

/* -------------------------------------------------------
   HELPER FUNCTION
------------------------------------------------------- */

/**
 * Resolves product availability for the current user's cluster.
 *
 * @param productName - Product identifier to check
 *
 * @returns {
 *   exists: boolean;      // Whether product exists in cluster
 *   status?: ProductStatus; // "active" | "inactive" if exists
 * }
 *
 * Behavior:
 * ----------
 * - If cluster is not defined → exists: false
 * - If cluster not found in config → exists: false
 * - If product not found in cluster → exists: false
 * - If product found → exists: true + status
 */
export function getClusterProductStatus(
  productName: ProductKey
): {
  exists: boolean;
  status?: ProductStatus;
} {
  const userStore = useUserStore();

  // Resolve current user's cluster from auth state
  const userCluster =
    userStore.auth.account.current?.cluster as ClusterKey | undefined;

  // If cluster not available → product does not exist
  if (!userCluster) {
    return { exists: false };
  }

  // Get cluster configuration
  const clusterProducts = CLUSTER_PRODUCT_MAP[userCluster];

  // If cluster not configured → product does not exist
  if (!clusterProducts) {
    return { exists: false };
  }

  // Get product status
  const status = clusterProducts[productName];

  // If product not present in cluster → does not exist
  if (!status) {
    return { exists: false };
  }

  // Product exists in cluster
  return {
    exists: true,
    status,
  };
}